import ReactDOM from "react-dom/client";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import "./lib/i18n";
import { I18nLanguageProvider } from "./lib/i18n-language-provider.tsx";
import { PostHogProvider } from "posthog-js/react";
// Import the generated route tree
import { routeTree } from "./routeTree.gen";
import { SnackbarProvider } from "./components/snackbar-provider";
import "./styles.css";
import reportWebVitals from "./reportWebVitals.ts";
import { LoadingSpinner } from "./components/spinner.tsx";
import { env } from "./lib/env.ts";
import { ModalProvider } from "./components/modal.tsx";
import posthog from "posthog-js";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { loadFeatureFlags } from "./lib/runtime-config.ts";
import { applyCachedDynamicFeatureFlags } from "./lib/dynamic-feature-flags.ts";
import { initRecyclerFillEvents } from "./lib/recycler-fill-events.ts";
import { kioskTalkController } from "./features/kiosk-talk/kiosk-talk.tsx";
import * as Sentry from "@sentry/react";
import { initGoogleTagManager } from "./lib/google-tag-manager.ts";

Sentry.init({
  dsn: "https://66f6346d9120352c9022f10e5fcaaccd@o4508481603895296.ingest.de.sentry.io/4511172129718352",
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
});

initGoogleTagManager();

// Create a client
const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      console.log("capturing error...");
      Sentry.captureException(error);
      posthog.capture("query-error", {
        client_id: localStorage.getItem("client_id"),
        kioskKey: localStorage.getItem("kioskKey"),
        error,
        message: error?.message,
      });
      console.error(error);
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      console.log("capturing mutation error...");
      Sentry.captureException(error);
    },
  }),
});

// The query cache used to be persisted here; drop the multi-MB leftover.
localStorage.removeItem("REACT_QUERY_OFFLINE_CACHE");

// Create a new router instance
const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  scrollRestoration: true,
  defaultStructuralSharing: true,
  defaultPreloadStaleTime: 0,
  defaultPendingComponent: () => <LoadingSpinner />,
  context: {
    queryClient,
  },
});

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const useMocks = false;

async function enableMocking() {
  if (!useMocks || !document.location.href.includes("localhost:3017")) {
    return;
  }

  const { worker } = await import("./mocks/worker.ts");

  return worker.start({ onUnhandledRequest: "bypass" });
}

// Apply last-known backend feature flags synchronously so the first render (and
// offline boots) use the most recent values. The start screen refreshes them
// from the backend whenever there's a connection.
applyCachedDynamicFeatureFlags();

loadFeatureFlags()
  .then(() => {
    initRecyclerFillEvents();
    kioskTalkController.connect();
    return enableMocking();
  })
  .then(() => {
    const rootElement = document.getElementById("app");
    if (rootElement && !rootElement.innerHTML) {
      const root = ReactDOM.createRoot(rootElement);
      root.render(
        <PostHogProvider
          apiKey={env.VITE_PUBLIC_POSTHOG_KEY}
          options={{
            api_host: env.VITE_PUBLIC_POSTHOG_HOST,
            defaults: "2025-05-24",
            capture_exceptions: true, // This enables capturing exceptions using Error Tracking, set to false if you don't want this
          }}
        >
          <QueryClientProvider client={queryClient}>
            {false && <ReactQueryDevtools initialIsOpen={false} />}
            <I18nLanguageProvider>
              <SnackbarProvider>
                <ModalProvider>
                  <RouterProvider router={router} />
                </ModalProvider>
              </SnackbarProvider>
            </I18nLanguageProvider>
          </QueryClientProvider>
        </PostHogProvider>,
      );
    }
  });

// Render the app

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

// [
//   "pointerover",
//   "pointerenter",
//   "pointerout",
//   "gotpointercapture",
//   "lostpointercapture",
//   "mousedown",
//   "mouseup",
//   "click",
//   "dblclick",
//   "mousemove",
//   "mouseover",
//   "mouseout",
//   "mouseenter",
//   "mouseleave",
//   "contextmenu",
//   "touchstart",
//   "touchend",
//   "touchmove",
//   "touchcancel",
//   "wheel",
//   "dragstart",
//   "drag",
//   "dragend",
//   "dragenter",
//   "dragleave",
//   "dragover",
//   "drop",
// ].forEach((eventType) => {
//   window.addEventListener(
//     eventType,
//     (e) => {
//       console.log(`${eventType}:`, {
//         x: e.clientX,
//         y: e.clientY,
//         pointerType: e.pointerType, // "mouse", "touch", or "pen"
//         pointerId: e.pointerId,
//         isPrimary: e.isPrimary,
//         target: e.target?.tagName,
//       });
//     },
//     { capture: true, passive: true },
//   );
// });
